import React, { lazy, Suspense } from 'react';
import { AuthorAvatar } from '../../avatarImage/AuthorAvatar';
// Loaded only where the badge is actually shown. It pulls in Spectrum's
// dialog, and most bands (browse heroes, the card dialog) never render it.
const NotAiDialog = lazy(() =>
    import('../../widgets/notAiDialog/NotAiDialog').then((m) => ({ default: m.NotAiDialog }))
);

const POWER_STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** Ten-step power meter, green through red; `lit` is how many steps light up. */
export function PowerMeter({ lit }) {
    return (
        <span className="reportMeter" aria-hidden="true">
            {POWER_STEPS.map((step) => (
                <span
                    key={step}
                    className={`reportMeterStep${step <= lit ? ' is-lit' : ''}${step >= 9 ? ' is-cedh' : ''}`}
                    style={{ '--step': step }}
                />
            ))}
        </span>
    );
}

/**
 * Avatar plus name. A button when `onPress` is given, plain text otherwise.
 * `lead` enlarges it for pages where the person is the subject.
 */
export function ReportPerson({ avatarUrl, name, prefix, onPress, lead = false, children }) {
    const className = `reportBandPerson${lead ? ' reportBandPerson--lead' : ''}`;
    const body = (
        <>
            <AuthorAvatar avatarUrl={avatarUrl} alt="" className="reportBandAvatar" />
            <span className="reportBandPersonName">
                {prefix && <span className="reportBandPersonPrefix">{prefix}</span>} {name}
            </span>
            {children}
        </>
    );
    if (!onPress) return <span className={className}>{body}</span>;
    return (
        <button type="button" className={`buttonReset ${className}`} onClick={onPress}>
            {body}
        </button>
    );
}

/** A plain-language status box, e.g. a deck that couldn't be scored. */
/** A notice with a way out: something failed to load and can be asked for again. */
export function ReportRetryNotice({ title, children, onRetry }) {
    return (
        <div className="reportSectionError" role="alert">
            <h2 className="reportNoticeTitle">{title}</h2>
            <p className="reportNoticeBody">{children}</p>
            <button type="button" className="searedPrimaryBtn" onClick={onRetry}>
                Try again
            </button>
        </div>
    );
}

export function ReportNotice({ title, children }) {
    return (
        <div className="reportNotice" role="status">
            <h2 className="reportNoticeTitle">{title}</h2>
            <p className="reportNoticeBody">{children}</p>
        </div>
    );
}

/**
 * One verdict. A tile tied to a section is a button that opens it (lit gold
 * while that section is open); any other tile is a plain readout.
 *
 * `reserve` holds an empty line where a sibling tile has a sub-label or a
 * detail line, so Power ("Competitive") and Salt (nothing) still bottom out
 * at the same height instead of stepping down mid-row.
 */
function Tile({ tile, activeSection, onSelectSection, reserve = {} }) {
    const interactive = Boolean(tile.section && onSelectSection);
    const active = interactive && activeSection === tile.section;
    const content = (
        <>
            <span className="reportTileLabel">{tile.label}</span>
            <span className={`reportTileValue${tile.valueTone ? ` reportTileValue--${tile.valueTone}` : ''}`}>
                {tile.value}
                {tile.marker && (
                    <sup className="reportTileMarker" aria-hidden="true">
                        {tile.marker}
                    </sup>
                )}
            </span>
            {tile.graphic}
            {(tile.sub || reserve.sub) && <span className="reportTileSub">{tile.sub || '\u00a0'}</span>}
            {(tile.detail || reserve.detail) && <span className="reportTileDetail">{tile.detail || '\u00a0'}</span>}
        </>
    );
    // `help` sits beside the tile, never inside it: an interactive tile is a
    // button, and a help trigger nested in a button is invalid and swallows
    // the click.
    const withHelp = (node) =>
        tile.help ? (
            <div className="reportTileWrap">
                {node}
                <span className="reportTileHelp">{tile.help}</span>
            </div>
        ) : (
            node
        );

    if (!interactive) return withHelp(<div className="reportTile">{content}</div>);
    return withHelp(
        <button
            type="button"
            className={`buttonReset reportTile reportTile--action${active ? ' is-active' : ''}`}
            aria-current={active ? 'true' : undefined}
            onClick={() => onSelectSection(tile.section)}
        >
            {content}
        </button>
    );
}

function Backdrop({ images }) {
    const art = (images || []).filter(Boolean);
    if (art.length === 0) return null;
    return (
        <div className="reportBandBackdrop" aria-hidden="true">
            {art.map((src) => (
                <img key={src} src={src} alt="" className="reportBandArt" />
            ))}
        </div>
    );
}

/**
 * The first read of a report page: optional card art behind the subject's
 * meta line, then its headline numbers as large verdict tiles. `children`
 * replaces the tiles (e.g. a notice when there are no numbers to show);
 * `loading` shows skeleton tiles while data is in flight.
 */
export function ReportBand({
    label,
    backdrop,
    meta,
    notAiBadge = false,
    tiles = [],
    activeSection,
    onSelectSection,
    footer,
    loading = false,
    loadingTiles = 4,
    children,
}) {
    const hasArt = (backdrop || []).filter(Boolean).length > 0;
    const reserve = { sub: tiles.some((t) => t.sub), detail: tiles.some((t) => t.detail) };
    return (
        <section
            className="reportBand"
            data-art={hasArt ? 'true' : undefined}
            aria-label={label}
            aria-busy={loading || undefined}
        >
            <Backdrop images={backdrop} />
            <div className="reportBandBody">
                {meta && <div className="reportBandMeta">{meta}</div>}
                {children ||
                    (loading ? (
                        <div className="reportTiles" data-count={loadingTiles}>
                            {Array.from({ length: loadingTiles }, (_, i) => (
                                <div key={i} className="reportTile reportTile--skeleton">
                                    <span className="reportTileLabel skeletonBlock">&nbsp;</span>
                                    <span className="reportTileValue skeletonBlock">&nbsp;</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        tiles.length > 0 && (
                            <div className="reportTiles" data-count={tiles.length}>
                                {tiles.map((tile) => (
                                    <Tile
                                        key={tile.key}
                                        tile={tile}
                                        activeSection={activeSection}
                                        onSelectSection={onSelectSection}
                                        reserve={reserve}
                                    />
                                ))}
                            </div>
                        )
                    ))}
                {(footer || notAiBadge) && (
                    <div className="reportBandFooter">
                        {footer}
                        {notAiBadge && (
                            <Suspense fallback={null}>
                                <NotAiDialog size="band" />
                            </Suspense>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
